/**
 * -----------------------------------------------------------------------------
 *  GDPR / CCPA CONSENT CONTROLLER – IMPORTANT IMPLEMENTATION NOTE
 * -----------------------------------------------------------------------------
 *
 *  This script MUST be placed directly inside the <head> of theme.liquid
 *  and executed inline (synchronously). It CANNOT be loaded as an external
 *  Shopify asset, deferred script, or asynchronously-loaded JS file.
 *
 *  REASON:
 *  - Google Tag Manager and Google Tag (GA4) require Consent Mode defaults
 *    (gtag('consent','default', ...)) to be set BEFORE GTM initializes.
 *  - If this script is loaded asynchronously, GTM may load early and evaluate
 *    tags before Consent Mode is applied — breaking GDPR/CCPA compliance.
 *  - This controller determines at page load whether GTM can be injected,
 *    based on GeoIP region, stored consentMode, and banner interaction.
 *  - Because these decisions must run BEFORE GTM loads, execution timing is
 *    critical and MUST occur inline in the HTML head.
 *
 *  VERSION CONTROL:
 *  - This file is kept in Git for versioning and code review.
 *  - However, its contents MUST be copied/pasted directly into theme.liquid
 *    <head> to ensure correct execution timing and legal compliance.
 *
 *  SUMMARY:
 *    ✔ Keep this file in Git for development.
 *    ✔ Copy its entire contents into theme.liquid <head>.
 *    ✔ Do NOT load it via {% javascript %}, asset_url, or external script tags.
 *    ✔ GTM will only load when consent or region conditions allow it.
 *
 *  This ensures:
 *    - Full GDPR / CCPA compliance
 *    - Proper Consent Mode initialization
 *    - Correct GTM blocking behavior until consent is granted
 *    - Stable and predictable tag firing across all Shopify templates
 *
 *   The GTM <noscript> iframe is intentionally omitted because it bypasses
 *   all consent logic and would load GTM without user permission (GDPR violation).
 * -----------------------------------------------------------------------------
 */


window.dataLayer = window.dataLayer || [];
localStorage.removeItem('hideBannerFlag');

// ---------------------------------------------------------------------------
//  CROSS-SUBDOMAIN CONSENT SYNC (Cookie <-> localStorage)
//  Allows sharing consent between www.getexoshield.com and shop.getexoshield.com
// ---------------------------------------------------------------------------

function setConsentCookie(value) {
  // Must include SameSite=None + Secure for modern browsers
  document.cookie =
    "consentMode=" + encodeURIComponent(value) +
    "; Path=/; Domain=.getexoshield.com; SameSite=None; Secure";
}

function getConsentCookie() {
  const match = document.cookie.match(/(^|;\s*)consentMode=([^;]+)/);
  return match ? decodeURIComponent(match[2]) : null;
}

function syncConsentFromCookie() {
  const cookieVal = getConsentCookie();
  const localVal  = localStorage.getItem("consentMode");

  // If cookie exists but localStorage doesn't → sync to localStorage
  if (cookieVal && !localVal) {
    localStorage.setItem("consentMode", cookieVal);
  }
}

function syncConsentToCookie() {
  const localVal = localStorage.getItem("consentMode");
  if (localVal) setConsentCookie(localVal);
}
syncConsentFromCookie();

function gtag() { dataLayer.push(arguments); }

function loadGTM() {
  if (window.gtmLoaded) return;
  (function(w, d, s, l, i) {
    w[l] = w[l] || [];
    w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
    const f = d.getElementsByTagName(s)[0];
    const j = d.createElement(s);
    const dl = l != 'dataLayer' ? '&l=' + l : '';
    j.async = true;
    j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
    f.parentNode.insertBefore(j, f);
  })(window, document, 'script', 'dataLayer', 'GTM-KJFTXM8P');
  window.gtmLoaded = true;
}

function applyGDPR() {
  const stored = localStorage.getItem('consentMode');
  const consent = stored ? JSON.parse(stored) : null;

  if (!stored) {
    gtag('consent', 'default', {
      ad_storage: 'denied',
      analytics_storage: 'denied',
      personalization_storage: 'denied',
      functionality_storage: 'denied',
      security_storage: 'denied'
    });
  } else {
    gtag('consent', 'update', consent);
  }
}

function geoip(json) {
  const countryCode = json.country_code || "Unknown";
  const region = json.region || "Unknown";

  const europeanCountries = [
    "AT","BE","BG","HR","CY","CZ","DK","EE","FI","FR","DE","GR","HU","IE","IT","LV","LT",
    "LU","MT","NL","PL","PT","RO","SK","SI","ES","SE","GB","IS","LI","NO","CH"
  ];

  const lastCountry = localStorage.getItem("lastCountry");
  if (lastCountry !== countryCode) {
    localStorage.setItem("lastCountry", countryCode);
    if (europeanCountries.includes(countryCode) || (countryCode === "US" && region === "California")) {
      localStorage.removeItem("hideBannerFlag");
      localStorage.removeItem("consentMode");
    } else {
      localStorage.setItem("hideBannerFlag", "true");
    }
  }

  if (europeanCountries.includes(countryCode) || (countryCode === "US" && region === "California")) {
    applyGDPR();
  } else {
    const granted = {
      ad_storage: 'granted',
      analytics_storage: 'granted',
      personalization_storage: 'granted',
      functionality_storage: 'granted',
      security_storage: 'granted'
    };
    gtag('consent', 'default', granted);
    localStorage.setItem("consentMode", JSON.stringify(granted));
    syncConsentToCookie(); // <— add this line
    loadGTM();
  }
}

function checkConsentOnLoad() {
  if (localStorage.getItem("consentMode")) loadGTM();
}

checkConsentOnLoad();
fetch("https://get.geojs.io/v1/ip/geo.json")
  .then(res => res.json())
  .then(geoip)
  .catch(err => {
    console.warn("GeoIP failed", err);
    applyGDPR();
    showBanner();
    loadGTM();
  });

